import { parseResponseDateAndTime } from 'src/common/utils/parseResponseDate';
import { Localization, SimDTO } from '../../constants/sim';
import {
  SessionStatus,
  SessionStatusUi,
  SESSION_STATUS,
  SESSION_STATUS_UI,
} from '../../constants/sessionStatus';
import { getLabelBaseOnMnoStatus, MNOStatus, MNO_STATUS } from '../../constants/mnoStatus';
import { NormilizedAssignedTags } from 'tags/models/tags';
import { TagsData } from 'src/tags/models/tagsApi.types';
import { ACTIVATION_ACTIONS } from 'src/common/components/Products/actions/constants/activationActionsConstants';
import { t } from 'i18next';
import { isNil, isNumber } from 'lodash';

type ExternalOptionalData = {
  normalizedTagsData?: NormilizedAssignedTags;
};

const getHomeNetworkTranslation = (profile: SimDTO) => {
  return profile.networkCodeTranslation?.mccMnc ?? profile.networkCodeTranslation?.plmn;
};

export const parseMNOStatusToDisplayValue = (mnoStatus: MNOStatus | null | undefined) => {
  if (!isNil(mnoStatus) && Object.values(MNO_STATUS).includes(mnoStatus)) {
    return getLabelBaseOnMnoStatus(mnoStatus, t);
  }

  return '';
};

export const parseLocalization = (localization: Localization | null | undefined) => {
  return localization?.value || '';
};

const parseNumberField = (value: unknown) => {
  if (typeof value === 'number' && isNumber(value)) {
    return value;
  }

  if (value === undefined) {
    return undefined;
  }

  return null;
};

export const mapSessionStatusToStringSessionStatus = (
  sessionStatusValue: SessionStatus | null | undefined,
): SessionStatusUi => {
  switch (sessionStatusValue) {
    case SESSION_STATUS.IN_SESSION:
      return SESSION_STATUS_UI.IN_SESSION;
    case SESSION_STATUS.DISCONNECTED:
      return SESSION_STATUS_UI.OUT_OF_SESSION;
    case undefined:
      return SESSION_STATUS_UI.NONE;
    default:
      return SESSION_STATUS_UI.UNKNOWN;
  }
};

export const mapCustomFields = (profile: SimDTO) => {
  const mappedCustomFields: { [key: string]: any } = {};
  profile.customFields?.forEach((field) => {
    if (mappedCustomFields[field.label.toLowerCase()]?.dataType === 'Dropdown') {
      const values = mappedCustomFields[field.label.toLowerCase()].selectionOption.split(', ');
      if (!values.includes(field.selectionOption)) {
        values.push(field.selectionOption);
      }
      mappedCustomFields[field.label.toLowerCase()].selectionOption = values.join(', ');
    } else {
      mappedCustomFields[field.label.toLowerCase()] = field;
    }
  });

  return mappedCustomFields;
};

const parseResponseTags = (iccid: string, externalOptionalData?: ExternalOptionalData) => {
  if (externalOptionalData?.normalizedTagsData?.[iccid]) {
    const { items, hasMore } = externalOptionalData.normalizedTagsData[iccid];
    return {
      items: items,
      hasMore: hasMore,
    };
  }

  return undefined;
};

const parseCumulativeTotalUsage = (
  cumulativeInputUsage: number | null | undefined,
  cumulativeOutputUsage: number | null | undefined,
) => {
  if (cumulativeInputUsage !== undefined || cumulativeOutputUsage !== undefined) {
    const isUsageNotNull = cumulativeInputUsage !== null || cumulativeOutputUsage !== null;
    return isUsageNotNull ? (cumulativeInputUsage || 0) + (cumulativeOutputUsage || 0) : null;
  }

  return undefined;
};

const parseResponseStringValue = <T extends string>(
  value: string | null | undefined,
  propsName: T,
): Record<T, string | undefined> => {
  if (value !== undefined) {
    return { [propsName as T]: value || '' } as Record<T, string | undefined>;
  }

  return {} as Record<T, string | undefined>;
};

const parseResponseDateValue = <T extends string>(
  value: string | null | undefined,
  propsName: T,
): Record<T, string | undefined> => {
  if (value !== undefined) {
    return { [propsName as T]: parseResponseDateAndTime(value) } as Record<T, string | undefined>;
  }

  return {} as Record<T, string | undefined>;
};

export const mapSimDTO = (profile: SimDTO, externalOptionalData?: ExternalOptionalData) => {
  const {
    accountRef,
    mnoStatus,
    mnoStatusEntryTime,
    simType,
    liveDataUsage,
    inboundLiveDataUsage,
    outboundLiveDataUsage,
    inMinuteUsage,
    inTextUsage,
    outMinuteUsage,
    outTextUsage,
    sessionStatus,
    sessionStartTime,
    sessionEndTime,
    sessionStatusLastUpdated,
    orderNumber,
    connectionId,
    apn,
    localization,
    rasClient,
    framedProtocol,
    nasIpAddress,
    serviceType,
    userName,
    cumulativeInputUsage,
    cumulativeOutputUsage,
    productCode,
    productType,
    activationAction,
    currentIpAddress,
    ...rest
  } = profile;

  const tags = parseResponseTags(rest.iccid, externalOptionalData);
  const homeNetworkTranslation = getHomeNetworkTranslation(profile);

  const cumulativeTotalUsage = parseCumulativeTotalUsage(
    cumulativeInputUsage,
    cumulativeOutputUsage,
  );

  const mappedCustomFields = mapCustomFields(profile);
  return {
    ...rest,
    ...(accountRef !== undefined && { accountRef: accountRef?.toString() || '' }),
    ...parseResponseStringValue(homeNetworkTranslation?.countryName ?? rest.countryName, 'homeNetworkCountryName'),
    ...parseResponseStringValue(homeNetworkTranslation?.isoCode ?? rest.isoCode, 'homeNetworkIsoCode'),
    ...parseResponseStringValue(rest.region, 'homeNetworkRegion'),
    ...parseResponseStringValue(homeNetworkTranslation?.operatorName, 'homeNetworkOperatorName'),
    ...parseResponseStringValue(homeNetworkTranslation?.tadig ?? rest.tadig, 'homeNetworkTadig'),
    ...parseResponseStringValue(homeNetworkTranslation?.mnc ?? rest.mnc, 'homeNetworkMnc'),
    ...parseResponseStringValue(homeNetworkTranslation?.mcc ?? rest.mcc, 'homeNetworkMcc'),
    ...parseResponseStringValue(homeNetworkTranslation?.plmn ?? rest.plmn, 'homeNetworkPlmn'),

    ...parseResponseDateValue(mnoStatusEntryTime, 'mnoStatusEntryTime'),
    ...(mnoStatus !== undefined && {
      mnoStatus: parseMNOStatusToDisplayValue(mnoStatus),
    }),
    ...(sessionStatus !== undefined && {
      sessionStatus: mapSessionStatusToStringSessionStatus(sessionStatus),
    }),

    liveDataUsage: parseNumberField(liveDataUsage),
    inboundLiveDataUsage: parseNumberField(inboundLiveDataUsage),
    outboundLiveDataUsage: parseNumberField(outboundLiveDataUsage),
    inTextUsage: parseNumberField(inTextUsage),
    outTextUsage: parseNumberField(outTextUsage),
    inMinuteUsage: parseNumberField(inMinuteUsage),
    outMinuteUsage: parseNumberField(outMinuteUsage),
    ...parseResponseDateValue(sessionStartTime, 'sessionStartTime'),
    ...parseResponseDateValue(sessionEndTime, 'sessionEndTime'),
    ...parseResponseDateValue(sessionStatusLastUpdated, 'sessionStatusLastUpdated'),
    ...parseResponseStringValue(orderNumber, 'orderNumber'),
    ...parseResponseStringValue(connectionId, 'connectionId'),
    ...parseResponseStringValue(apn, 'apn'),
    ...parseResponseStringValue(rasClient, 'rasClient'),
    ...parseResponseStringValue(framedProtocol, 'framedProtocol'),
    ...parseResponseStringValue(nasIpAddress, 'nasIpAddress'),
    ...parseResponseStringValue(serviceType, 'serviceType'),
    ...parseResponseStringValue(userName, 'userName'),
    ...parseResponseStringValue(productCode, 'productCode'),
    ...parseResponseStringValue(productType, 'productType'),
    ...parseResponseStringValue(currentIpAddress, 'currentIpAddress'),
    ...parseResponseStringValue(simType, 'simType'),
    ...(localization !== undefined && { localization: parseLocalization(localization) || '' }),
    ...(cumulativeInputUsage !== undefined && {
      cumulativeInputUsage: cumulativeInputUsage?.toFixed(2) || '',
    }),
    ...(cumulativeOutputUsage !== undefined && {
      cumulativeOutputUsage: cumulativeOutputUsage?.toFixed(2) || '',
    }),
    ...(cumulativeTotalUsage !== undefined && {
      cumulativeTotalUsage: cumulativeTotalUsage?.toFixed(2) || '',
    }),
    ...(activationAction !== undefined && {
      activationAction: activationAction?.toLocaleLowerCase() || ACTIVATION_ACTIONS.NONE,
    }),
    ...mappedCustomFields,
    tags: tags as TagsData,
    rawData: {
      sessionStatusLastUpdated,
    },
  };
};
